// Set an extension's SIP password.
//
// This exists for one shape of job: a Calix router's built-in ATA is configured on
// the router by hand and refuses anything over 12 characters, so the PBX has to be
// set to the value the router already holds. Verso writes it to FreePBX, confirms
// the PBX really took it, then tells a Grandstream phone through GDMS.
//
// Two ways in, and they're not symmetric. Typing one is the router case — the value
// exists already, so we never echo it back. Generating one is the new-line case, so
// the value is shown ONCE, because it has to be typed into the router.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

const CALIX_MAX = 12;

export default function SetSipPasswordModal({ sipAccountId, username, onClose, onSaved }) {
  const [mode, setMode] = useState('type');     // 'type' | 'generate'
  const [secret, setSecret] = useState('');
  const [generated, setGenerated] = useState(null);
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: (body) => api.put(`/sip-accounts/${sipAccountId}/secret`, body),
    onSuccess: (res) => {
      // A generated password is the one thing that must survive this dialog: it
      // isn't recoverable without another audited reveal, and it has to reach the
      // router.
      if (res?.secret) { setGenerated(res); return; }
      onSaved?.(res);
    },
    onError: (err) => setError(err?.body?.error || err?.message || 'Could not set the password'),
  });

  if (generated) {
    return (
      <Shell title={`SIP password set for ${username}`} onClose={() => { setGenerated(null); onSaved?.(generated); }}>
        {/* CONFIRMED IS NOT A GIVEN. This said "The PBX has it and confirmed it" unconditionally,
            which is false on a host that cannot be read back: an Aria PBX does not serve SIP
            passwords at all, so the write is authoritative but unverifiable. Saying it was
            confirmed there is the same overclaim the verification vocabulary exists to prevent. */}
        <p className="mt-1 text-sm text-gray-600">
          {generated.verifiable === false
            ? 'The PBX accepted it. It cannot report passwords back, so Verso holds the copy of record and nothing re-read it. '
            : 'The PBX has it and confirmed it. '}
          Copy it onto the phone or router now — it won&rsquo;t be shown again without an audited reveal.
        </p>
        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-lg tracking-wide">
          {generated.secret}
        </div>
        {generated.calix?.length > 0 && (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {generated.calix.map((c) => c.fsan || c.mac).join(', ')} is a Calix router — its built-in ATA is
            configured on the router, so set this same password there. Nothing was pushed to it.
          </p>
        )}
        {generated.gdms?.attempted && !generated.gdms.ok && (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            The PBX is set, but the phone couldn&rsquo;t be told through GDMS ({generated.gdms.error}). It will
            keep failing to register until it gets this password.
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => { setGenerated(null); onSaved?.(generated); }}
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </Shell>
    );
  }

  const tooLongForCalix = mode === 'type' && secret.length > CALIX_MAX;
  const canSubmit = (mode === 'generate' || secret.trim().length >= 10) && !save.isPending;

  return (
    <Shell title={`Set SIP password — ${username}`} onClose={onClose}>
      <p className="mt-1 text-sm text-gray-600">
        Written to the PBX, read back to confirm it took, then pushed to the phone through GDMS.
        The phone stops registering until it has the new password.
      </p>

      {/* THE NOTICE THAT OUTLIVED THE PROBLEM IT DESCRIBED.
          This said "only on hosts running the newest agent … an agent release that is built but not
          yet deployed", and sent an operator to "set it in FreePBX there, then use Import secrets".
          All three parts are now false, checked on 2026-09-09: pbx-01, pbx-02 and pbx-04 all run
          0.62.1 and all three advertise `extension-secret-write`. Worse, NONE of them advertises
          `extension-secret-read` any more, so Import secrets cannot read a password back either —
          the fallback it recommended is the one thing that definitely will not work.

          What is true before you click is what the paragraph above already says: the write lands on
          the PBX and the phone is told through GDMS. So this says the part that is a caution rather
          than a description — the device Verso cannot reach. A Calix router's built-in ATA is
          configured on the router by hand, and the endpoint refuses a password it could not hold
          (the 12-character ceiling) rather than taking the line off the air. */}
      <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <strong>A router configured by hand needs this typed into it too.</strong> A Grandstream
        phone is re-pushed automatically through GDMS. A Calix router’s built-in ATA is not — it
        holds its own copy, so the new password has to be entered on the router or that line stops
        registering.
      </p>

      <div className="mt-4 flex gap-2 text-sm">
        {[
          ['type', 'Use a password I have'],
          ['generate', 'Generate one'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setMode(key); setError(null); }}
            className={`rounded-md px-3 py-1.5 font-medium ${
              mode === key ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'type' ? (
        <div className="mt-3">
          <label htmlFor="sip-password" className="block text-sm font-medium text-gray-700">Password</label>
          <input
            id="sip-password"
            type="text"
            value={secret}
            onChange={(e) => { setSecret(e.target.value); setError(null); }}
            autoComplete="off"
            spellCheck={false}
            placeholder="the password already on the router"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            10 characters or more, with upper case, lower case and digits. A Calix router&rsquo;s built-in ATA
            won&rsquo;t accept more than {CALIX_MAX}.
          </p>
          {tooLongForCalix && (
            <p className="mt-1 text-xs text-amber-700">
              Longer than {CALIX_MAX} characters — fine for a Grandstream phone, refused by a Calix ATA.
            </p>
          )}
        </div>
      ) : (
        <p className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          Verso will generate a {CALIX_MAX}-character password a Calix ATA accepts, and show it once so you
          can put it on the device.
        </p>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={save.isPending}
          className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            setError(null);
            save.mutate(mode === 'generate' ? { generate: true } : { secret: secret.trim() });
          }}
          className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {save.isPending ? 'Setting on the PBX…' : 'Set password'}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600">Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}
